/**
 * utmCapture — first-touch + last-touch UTM attribution helper.
 *
 * Reads `utm_*` query params from the current URL on every page load,
 * persists them in localStorage as a single JSON blob with both the
 * first-touch (set once on the visitor's first visit) and last-touch
 * (overwritten every time a new utm_source appears) snapshots.
 *
 * The persisted shape attaches to every subsequent analytics event the
 * factory emits so the warehouse can attribute conversions back to
 * source campaigns.
 *
 * @module @puffin-core/marketing-shell/analytics/utmCapture
 */

const UTM_FIELDS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
]);

const DEFAULT_STORAGE_KEY = 'marketingAttribution';

/**
 * @typedef {Object} AttributionRecord
 * @property {Object} firstTouch       UTM params captured on the visitor's first visit.
 * @property {string} firstTouchAt     ISO-8601 timestamp.
 * @property {Object} lastTouch        UTM params from the most recent visit that carried any utm_*.
 * @property {string} lastTouchAt      ISO-8601 timestamp.
 */

/**
 * Extract UTM params from a URL string. Returns an empty object when
 * none are present.
 *
 * @param {string} url       Full URL (location.href or any absolute URL).
 * @returns {Object<string, string>}
 */
export function extractUtmParams(url) {
  if (typeof url !== 'string' || !url) return {};
  let parsed;
  try {
    parsed = new URL(url, 'https://placeholder.local');
  } catch (error) {
    // `new URL` answers an unparsable address with a TypeError — an address that cannot be read
    // carries no campaign parameters. Anything else is a defect. Read by NAME, not `instanceof`:
    // a URL implementation from another realm (jsdom's in the test environment) throws that
    // realm's TypeError.
    if (error?.name !== 'TypeError') throw error;
    return {};
  }
  const out = {};
  for (const field of UTM_FIELDS) {
    const v = parsed.searchParams.get(field);
    if (v) out[field] = v;
  }
  return out;
}

/**
 * Read the persisted attribution record (or null when none).
 *
 * @param {Object} opts
 * @param {Object} opts.storageAdapter   Object with .get(key) -> string|null.
 * @param {string} [opts.storageKey]
 * @returns {AttributionRecord|null}
 */
export function readAttribution({ storageAdapter, storageKey = DEFAULT_STORAGE_KEY } = {}) {
  if (!storageAdapter || typeof storageAdapter.get !== 'function') return null;
  // The storage adapter absorbs the browser refusing storage (`createLocalStorageAdapter`), so a
  // throw from `get` is a defect and is not caught here.
  const raw = storageAdapter.get(storageKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    // Stored text that is not JSON (written by an older build, or edited by hand) is no record.
    if (!(error instanceof SyntaxError)) throw error;
    return null;
  }
}

/**
 * Capture (or update) the persisted attribution record from a URL.
 *
 * - First call ever writes both firstTouch + lastTouch from the URL.
 * - Subsequent calls update only lastTouch when the URL carries new
 *   utm_* params; otherwise the record stays untouched.
 *
 * @param {Object} opts
 * @param {string} opts.url
 * @param {Object} opts.storageAdapter   Object with .get(key) + .set(key, value).
 * @param {string} [opts.storageKey]
 * @param {() => Date} [opts.now]        Injectable clock for tests.
 * @returns {AttributionRecord|null}     The record AFTER capture, or null if no UTM was present and no prior record.
 */
export function captureAttribution({
  url,
  storageAdapter,
  storageKey = DEFAULT_STORAGE_KEY,
  now = () => new Date(),
}) {
  if (!storageAdapter || typeof storageAdapter.set !== 'function') return null;
  const params = extractUtmParams(url);
  const existing = readAttribution({ storageAdapter, storageKey });
  if (Object.keys(params).length === 0) {
    return existing || null;
  }
  const stamp = now().toISOString();
  const next = existing
    ? {
        ...existing,
        lastTouch: params,
        lastTouchAt: stamp,
      }
    : {
        firstTouch: params,
        firstTouchAt: stamp,
        lastTouch: params,
        lastTouchAt: stamp,
      };
  // The storage adapter absorbs a refused write (private browsing / a full quota): the record
  // is returned either way and the next call rewrites it. A throw here is a defect.
  storageAdapter.set(storageKey, JSON.stringify(next));
  return next;
}

export { UTM_FIELDS, DEFAULT_STORAGE_KEY };
